import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Dimmer, Loader } from 'semantic-ui-react';
import { useMutation } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toastMessage from '../../utils/toastMessage';
import {
  ISlackOAuthCallbackMutation,
  ISlackOAuthCallbackMutationVariables,
} from '../../utils/apollo/graphqlSchema';
import printGraphqlErrors from '../../utils/apollo/printGraphqlErrors';
import { z } from 'zod';
import useBroadcastChannel from '../../utils/hooks/useBroadcastChannel';
import { noop } from 'lodash';
import { SLACK_OAUTH_CALLBACK } from '../../utils/apollo/slack/mutations';
import { MaybeNil } from '../../utils/interfaces';
import { appRoutes } from '../../path';

const callbackSuccessSchema = z.object({
  code: z.string(),
  state: z.string(),
});

const callbackErrorSchema = z.object({
  errorDescription: z.string().nullish(),
  error: z.string(),
});

const FixedDimmer = styled(Dimmer)`
  &&& {
    position: fixed;
  }
`;

export default () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // message handler must be stable reference, otherwise it causes effect
  // in useBroadcastChannel on every rerender
  const { broadcast } = useBroadcastChannel<MaybeNil<string>>({
    channelName: 'slack',
    messageHandler: noop,
  });

  const [executeSlackOAuthCallback, { loading }] = useMutation<
    ISlackOAuthCallbackMutation,
    ISlackOAuthCallbackMutationVariables
  >(SLACK_OAUTH_CALLBACK, {
    onCompleted: (response) => {
      const result = response.slackOAuthCallback;
      if (!printGraphqlErrors(result.errors)) {
        const message = result.message ?? 'Slack connection was successful';

        toastMessage({
          message,
          type: 'info',
        });

        // broadcast will refresh current user to refresh the slackInstallation
        // property (see WsNotification)
        broadcast(message);

        window.close();
      }

      // if window.close didn't close the window (e.g. when connecting via
      // external link, not within the app or we didn't open the callback
      // window via window.open), fallback to open personal preferences where
      // Slack management is configurable
      navigate(appRoutes.settings.preferences);
    },
  });

  const callbackRef = useRef(async () => {
    const callbackParams = callbackSuccessSchema.safeParse({
      code: searchParams.get('code'),
      state: searchParams.get('state'),
    });

    if (callbackParams.success) {
      await executeSlackOAuthCallback({
        variables: {
          input: callbackParams.data,
        },
      });
    } else {
      const errorParams = callbackErrorSchema.safeParse({
        error: searchParams.get('error'),
        errorDescription: searchParams.get('error_description'),
      });

      let message = 'Failed to connect to Slack';
      if (errorParams.success) {
        message += `. ${errorParams.data.errorDescription ?? `Error code: ${errorParams.data.error}`}`;
      }

      toastMessage({
        message,
        type: 'error',
      });

      navigate(appRoutes.settings.preferences);
    }
  });

  // execute once - on mount
  useEffect(() => {
    callbackRef.current();
  }, []);

  return loading ? (
    <FixedDimmer active>
      <Loader />
    </FixedDimmer>
  ) : null;
};
